import { useState } from 'preact/hooks';
import type { PlayerPresence } from '@knight/shared';
import * as S from '../app/state';
import { t, tn } from '../i18n';
import { Btn, Avatar, HeroPortrait, copyText, origin } from './common';
import { api } from '../net/api';
import { game } from '../game/client';
import { playSelectedSlot } from './Home';

/** Show mode: look a player up by nickname, then watch the run or join the same realm. */
export function PlayerLookup(props: { compact?: boolean }) {
  const [name, setName] = useState('');
  const [pr, setPr] = useState<PlayerPresence | null>(null);
  const [busy, setBusy] = useState(false);
  const online = S.conn.value === 'online';

  async function lookup() {
    const n = name.trim();
    if (n.length < 2) return;
    setBusy(true);
    try {
      setPr(await api.player(n));
    } catch (e) {
      const key = (e as { key?: string }).key;
      S.showToast(key && t(key) !== key ? t(key) : t('toast.error', { msg: (e as Error).message }));
      setPr(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="col" style={{ gap: 6 }}>
      <div class="row">
        <input
          class="text grow"
          value={name}
          maxLength={16}
          placeholder={t('label.findPlayer')}
          disabled={!online}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void lookup();
          }}
        />
        <Btn small disabled={!online || busy || name.trim().length < 2} onClick={() => void lookup()}>
          {t('btn.find')}
        </Btn>
      </div>
      {!online && !props.compact && <span class="small muted">{t('toast.needServer')}</span>}
      {pr && (
        <div class="presence panel">
          <div class="row">
            <Avatar i={pr.avatar} />
            <div class="col grow" style={{ gap: 1 }}>
              <b>{pr.name}</b>
              <span class="small muted">
                {pr.hidden ? t('label.playerHidden') : pr.realm ? t('label.inRealmCode', { code: pr.realm }) : pr.online ? t('label.online') : t('label.offline')}
                {pr.showMode && !pr.hidden ? ` · ${t('label.showModeOn')}` : ''}
                {pr.watchers ? ` · 👁 ${pr.watchers}` : ''}
              </span>
            </div>
            {pr.heroId && <HeroPortrait hero={pr.heroId} size={36} />}
          </div>
          {pr.realm && pr.heroId && (
            <div class="small muted">
              {tn('hero', pr.heroId)} · {t('label.lvl')} {pr.level} · {t('label.rankShort')} {pr.rank}
            </div>
          )}
          {pr.realm && (
            <div class="row">
              <Btn small primary onClick={() => game.watch(pr.name)}>
                {t('btn.watch')}
              </Btn>
              <Btn
                small
                onClick={() => {
                  S.pendingRealm.value = pr.realm!;
                  playSelectedSlot();
                }}
              >
                {t('btn.joinRealm')}
              </Btn>
              <Btn
                small
                ghost
                onClick={() => {
                  void copyText(`${origin()}#/w/${encodeURIComponent(pr.name)}`);
                  S.showToast(t('toast.copied'));
                }}
              >
                {t('btn.copy')}
              </Btn>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
